/**
 * Which node-config fields consume a bound list whole instead of fanning it out.
 *
 * The backend half of this is `BaseConfigDisplay.expands_leaf_refs`, read by
 * `flow_v2.resolution.container_expand_fields`: a container display that expands
 * turns a list-bound nested leaf into per-item runs, while one that does not gets
 * `resolve_refs_deep`, which hands the whole list to the node. That flag is a
 * ClassVar and is never serialized, so the editor mirrors it by display type —
 * adding a container display means adding it here in the same change.
 *
 * Two consequences follow for a field named here, and both are the editor's job.
 * The picker must not offer a source nested deeper than the field declares, since
 * nothing will ever spread it (the backend rejects the list instead). And such a
 * ref must not flip the node into iterate mode, since the run stays scalar and
 * the extra output level would be a depth the node never produces.
 *
 * Only `config_table_filter_rows` is listed. `config_bindable_dict_rows` and
 * `config_structured_fields` describe the same behaviour in their own docstrings,
 * but making them agree changes how their nodes iterate today, which belongs in
 * its own change.
 */
const REF_CONSUMING_CONFIG_DISPLAYS = new Set(["config_table_filter_rows"]);

type ConfigDisplayEntry = { display?: { type?: string } };

/** Names of the config fields in `config` that consume a bound list whole. */
export function refConsumingConfigNames(
  config: Record<string, ConfigDisplayEntry> | null | undefined
): Set<string> {
  const names = new Set<string>();
  for (const [name, entry] of Object.entries(config ?? {})) {
    const type = entry?.display?.type;
    if (type && REF_CONSUMING_CONFIG_DISPLAYS.has(type)) names.add(name);
  }
  return names;
}

/**
 * Whether the config field an input path belongs to consumes a bound list whole.
 *
 * `inputName` may address an operand nested inside the field
 * (`advanced_filters.0.value`), so the field is its first segment.
 */
export function inputConsumesRefsWhole(
  config: Record<string, ConfigDisplayEntry> | null | undefined,
  inputName: string | undefined
): boolean {
  if (!inputName) return false;
  return refConsumingConfigNames(config).has(inputName.split(".")[0]);
}
